// src/lib/mso-calculator-form.ts
//
// ★★ ANNEX A 22 — the calculator's form model: what an officer TYPES becomes the exact JSON body
// the route parses, and an Authority case loads back into the form.
//
// ══ THE RULE THIS FILE EXISTS TO HOLD ══
//
// The route refuses `totalCents: "155.20"` and `155.2` as INVALID_INPUT (Review Focus 2): money
// is whole integer cents on the wire and nothing is silently converted. So the conversion from
// the dollars an officer types happens HERE, once, by string arithmetic (`dollarsToCents` splits
// on the decimal point; there is no `parseFloat(x) * 100` anywhere), and a figure that cannot
// become whole cents is reported against the field and never sent.
//
// ⚠ A field left blank is OMITTED from the body, never sent as an empty string or a guessed
// default. The engine distinguishes "not supplied" (INCOMPLETE_INPUT, naming the field) from a
// supplied `false`, and a form that filled a default would turn the first into the second.
import { centsToDollarsText, dollarsToCents } from './mso-vocabulary'

export const ENUM_FIELDS = [
  'beneficiaryType',
  'careSetting',
  'visitType',
  'hspType',
  'capStatus',
  'billIndicator',
  'specialBillCondition',
] as const
export const TEXT_FIELDS = ['eligibleWardClass', 'billedWardClass'] as const
export const BOOLEAN_FIELDS = ['governmentReferral', 'voluntaryWardUpgrade'] as const

export interface ComponentRowForm {
  readonly component: string
  /** Dollars as typed, e.g. "135.20". */
  readonly amount: string
  /** Whole count as typed, or blank. Recorded by the engine; amounts are totals (clarification C9). */
  readonly occurrences: string
}

export interface CalculatorForm {
  readonly beneficiaryRef: string
  /** Enum, text and tri-state boolean fields by name: '' = not supplied, 'true' / 'false' for booleans. */
  readonly fields: Readonly<Record<string, string>>
  readonly treatmentDate: string
  readonly entitlementScheme: string
  readonly validFrom: string
  /** Blank means an open-ended entitlement (`validUntil: null`). */
  readonly validUntil: string
  readonly total: string
  /** False while the total simply follows the sum of the components. */
  readonly totalTouched: boolean
  readonly components: readonly ComponentRowForm[]
}

export function blankForm(): CalculatorForm {
  return {
    beneficiaryRef: '',
    fields: {},
    treatmentDate: '',
    entitlementScheme: 'MSO',
    validFrom: '',
    validUntil: '',
    total: '',
    totalTouched: false,
    components: [{ component: 'BILL_AMOUNT', amount: '', occurrences: '' }],
  }
}

/** The sum of the component amounts that parse, in whole cents; null when none can be summed. */
export function componentsSumCents(components: readonly ComponentRowForm[]): number | null {
  let sum = 0
  let any = false
  for (const row of components) {
    const parsed = dollarsToCents(row.amount)
    if ('cents' in parsed) {
      sum += parsed.cents
      any = true
    }
  }
  return any ? sum : null
}

/**
 * What the total field SHOWS: the officer's own text once touched (even if they cleared it), otherwise the
 * components' sum. A cleared box stays visibly empty while they type; {@link effectiveTotal} is what is SENT.
 */
export function displayedTotal(form: CalculatorForm): string {
  if (form.totalTouched) return form.total
  const sum = componentsSumCents(form.components)
  return sum === null ? '' : centsToDollarsText(sum)
}

/**
 * The total that is sent. The hint under the field says it follows the components, so a total that is blank,
 * touched or not, falls back to their sum rather than being refused for being empty.
 */
export function effectiveTotal(form: CalculatorForm): string {
  if (form.totalTouched && form.total.trim().length > 0) return form.total
  const sum = componentsSumCents(form.components)
  return sum === null ? '' : centsToDollarsText(sum)
}

export type BuiltRequest =
  | { readonly request: Record<string, unknown>; readonly errors?: undefined }
  | { readonly request?: undefined; readonly errors: Readonly<Record<string, string>> }

/**
 * The body for `POST /api/mso-calculations`, or the per-field reasons it cannot be built.
 * The error keys are the SAME names the route's refusal uses (`totalCents`,
 * `components[0].amountCents`), so a form error and a server refusal highlight the same input.
 */
export function buildMsoRequest(form: CalculatorForm): BuiltRequest {
  const errors: Record<string, string> = {}
  const body: Record<string, unknown> = { scheme: 'MSO' }

  if (form.beneficiaryRef.trim().length > 0) body['beneficiaryRef'] = form.beneficiaryRef.trim()
  for (const name of [...ENUM_FIELDS, ...TEXT_FIELDS]) {
    const value = (form.fields[name] ?? '').trim()
    if (value.length > 0) body[name] = value
  }
  for (const name of BOOLEAN_FIELDS) {
    const value = form.fields[name] ?? ''
    if (value === 'true') body[name] = true
    else if (value === 'false') body[name] = false
  }
  if (form.treatmentDate.length > 0) body['treatmentDate'] = form.treatmentDate

  body['entitlement'] = {
    scheme: form.entitlementScheme.trim(),
    validFrom: form.validFrom,
    validUntil: form.validUntil.length > 0 ? form.validUntil : null,
  }

  const total = dollarsToCents(effectiveTotal(form))
  if ('cents' in total) body['totalCents'] = total.cents
  else errors['totalCents'] = total.error

  const lines: Record<string, unknown>[] = []
  form.components.forEach((row, index) => {
    const amount = dollarsToCents(row.amount)
    const line: Record<string, unknown> = { component: row.component }
    if ('cents' in amount) line['amountCents'] = amount.cents
    else errors[`components[${index}].amountCents`] = amount.error
    const occurrences = row.occurrences.trim()
    if (occurrences.length > 0) {
      if (/^\d{1,9}$/.test(occurrences)) line['occurrences'] = Number(occurrences)
      else errors[`components[${index}].occurrences`] = 'Enter a whole number of occurrences, or leave it blank.'
    }
    lines.push(line)
  })
  body['components'] = lines

  return Object.keys(errors).length > 0 ? { errors } : { request: body }
}

const asString = (value: unknown): string => (typeof value === 'string' ? value : '')

/** Load a stored case input (whole cents, as the engine takes it) back into the form (dollars). */
export function formFromInput(input: Record<string, unknown>): CalculatorForm {
  const fields: Record<string, string> = {}
  for (const name of [...ENUM_FIELDS, ...TEXT_FIELDS]) fields[name] = asString(input[name])
  for (const name of BOOLEAN_FIELDS) {
    const value = input[name]
    fields[name] = typeof value === 'boolean' ? String(value) : ''
  }
  const entitlement =
    typeof input['entitlement'] === 'object' && input['entitlement'] !== null
      ? (input['entitlement'] as Record<string, unknown>)
      : {}
  const lines = Array.isArray(input['components']) ? (input['components'] as Record<string, unknown>[]) : []
  const components: ComponentRowForm[] = lines.map((line) => ({
    component: asString(line['component']),
    amount: typeof line['amountCents'] === 'number' ? centsToDollarsText(line['amountCents']) : '',
    occurrences: typeof line['occurrences'] === 'number' ? String(line['occurrences']) : '',
  }))
  return {
    beneficiaryRef: asString(input['beneficiaryRef']),
    fields,
    treatmentDate: asString(input['treatmentDate']),
    entitlementScheme: asString(entitlement['scheme']) || 'MSO',
    validFrom: asString(entitlement['validFrom']),
    validUntil: asString(entitlement['validUntil']),
    total: typeof input['totalCents'] === 'number' ? centsToDollarsText(input['totalCents']) : '',
    // A loaded case keeps ITS total, so a case with a deliberately inconsistent total reproduces its refusal.
    totalTouched: typeof input['totalCents'] === 'number',
    components: components.length > 0 ? components : blankForm().components,
  }
}

/**
 * True when a refusal's field name belongs to the input called `name`:
 * `components[0].amountCents` belongs to `components` and to `components[0]`.
 */
export function refusalNames(fields: readonly string[], name: string): boolean {
  return fields.some((f) => f === name || f.startsWith(`${name}.`) || f.startsWith(`${name}[`))
}

const isRealDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

/**
 * The purely SYNTACTIC checks, made before anything is posted so that a mistake in the typing is never sent
 * and never stored as a REFUSED calculation. They say what the engine would say, under the same field names, so
 * a form error renders exactly as a server refusal would.
 *
 * Only syntax: a real date, the shape of a reference, a scheme code, at least one component, whole-cent
 * amounts. Anything about what the bill MEANS (a missing care setting, a total that disagrees with the
 * components) is the engine's to say, recorded and explained, so that is still sent.
 */
export function checkMsoForm(form: CalculatorForm): BuiltRequest {
  const errors: Record<string, string> = {}
  if (!isRealDate(form.treatmentDate)) {
    errors['treatmentDate'] = 'Enter the date of treatment as a real date, for example 2026-03-14.'
  }
  const ref = form.beneficiaryRef.trim()
  if (ref.length > 0 && !/^[A-Za-z0-9-]{1,40}$/.test(ref)) {
    errors['beneficiaryRef'] = 'A beneficiary reference is 1 to 40 letters, digits or hyphens. Leave it blank if there is none.'
  }
  if (!/^[A-Z]{2,8}$/.test(form.entitlementScheme.trim())) {
    errors['entitlement.scheme'] = 'The scheme is a code of 2 to 8 capital letters, for example MSO.'
  }
  if (!isRealDate(form.validFrom)) errors['entitlement.validFrom'] = 'Enter the date the entitlement starts as a real date.'
  if (form.validUntil.length > 0 && !isRealDate(form.validUntil)) {
    errors['entitlement.validUntil'] = 'Enter the date the entitlement ends as a real date, or leave it blank.'
  }
  if (form.components.length === 0) errors['components'] = 'Add at least one bill component.'

  const built = buildMsoRequest(form)
  const all = { ...errors, ...(built.errors ?? {}) }
  return Object.keys(all).length > 0 ? { errors: all } : built
}
